// In-memory content store, hydrated once from Supabase at app startup.
// Lets the many synchronous call sites (useAdminImage, pricing tables, reviews)
// serve real DB data without rewriting each into async. Admin writes call
// refresh() to re-pull and notify the UI via the existing window events.

import { getReviews } from './api/reviews';
import { getTeam } from './api/team';
import { getTreatments } from './api/treatments';
import { getAllPageContent } from './api/content';
import { getSettings } from './api/settings';
import type { Review, TeamMember, Treatment, SiteSettings } from '@/types/db';

interface StoreState {
  reviews: Review[];
  team: TeamMember[];
  treatments: Treatment[];
  // keyed `${page_slug}_${language}` -> { content_key: value }
  content: Record<string, Record<string, string>>;
  settings: SiteSettings | null;
  loaded: boolean;
}

const state: StoreState = {
  reviews: [],
  team: [],
  treatments: [],
  content: {},
  settings: null,
  loaded: false,
};

export function getStore(): Readonly<StoreState> {
  return state;
}

export function notify(): void {
  window.dispatchEvent(new Event('admin-content-updated'));
  window.dispatchEvent(new Event('admin-data-updated'));
}

// Pull everything fresh from Supabase into the in-memory store.
export async function refreshStore(): Promise<void> {
  try {
    const [reviews, team, treatments, contentRows, settings] = await Promise.all([
      getReviews().catch(() => []),
      getTeam().catch(() => []),
      getTreatments().catch(() => []),
      getAllPageContent().catch(() => []),
      getSettings().catch(() => null),
    ]);

    state.reviews = reviews;
    state.team = team;
    state.treatments = treatments;
    state.settings = settings;

    const content: Record<string, Record<string, string>> = {};
    for (const row of contentRows) {
      const k = `${row.page_slug}_${row.language}`;
      (content[k] ??= {})[row.content_key] = row.value ?? '';
    }
    state.content = content;
    state.loaded = true;
  } catch (err) {
    console.warn('Store hydration failed:', err);
    state.loaded = true; // unblock UI with fallbacks
  }
  notify();
}

// Call once at startup.
export function hydrateStore(): void {
  void refreshStore();
}
